import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import { formatGapPct } from './priceConcernUtils'

export function PriceConcernsSummaryBar({
  summary,
  loading,
  exporting,
  onExport,
}) {
  const avg =
    summary?.averageReportedGap == null
      ? '—'
      : formatGapPct(summary.averageReportedGap)

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: { xs: 2, md: 2.5 },
        mb: 2,
        boxShadow: '0 1px 2px rgba(20,20,30,0.04)',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
          <ReportProblemOutlinedIcon sx={{ color: 'primary.main', mt: 0.25 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: 18, md: 19 } }}>
              Price Concerns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review partner-reported purchase price gaps. This page is for triage only.
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ flexShrink: 0 }}
        >
          <Stat
            label="Not reviewed"
            value={loading ? '…' : summary?.notReviewedCount ?? 0}
            color="#A9600C"
          />
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Stat
            label="Reviewed"
            value={loading ? '…' : summary?.reviewedCount ?? 0}
            color="#157F4A"
          />
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Stat label="Avg. reported gap" value={loading ? '…' : avg} />
          <Button
            variant="contained"
            startIcon={
              exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            onClick={onExport}
            disabled={exporting}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              whiteSpace: 'nowrap',
            }}
          >
            Export to Excel
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

function Stat({ label, value, color }) {
  return (
    <Box sx={{ minWidth: 72 }}>
      <Typography
        sx={{
          fontSize: 11,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: color || 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  )
}
